'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
// Polling lives in ONE place, shared with MediaThumb (#646). The hero and the
// thumbnail render the wait very differently, but "has the image landed yet"
// is the same question and two copies of it would drift.
import { useGeneratedImage } from '@/components/ui/use-generated-image';
import styles from './recipe-image.module.css';

interface RecipeImageProps {
  /** Recipe UUID. Required for polling — omit (or omit `variantType`) to disable. */
  recipeId?: string;
  variantType?: 'quick' | 'classic' | 'bougie' | string | null;
  initialImageUrl?: string | null;
  alt: string;
  sizes: string;
  priority?: boolean;
  /** Smaller placeholder icon for card thumbnails vs hero. */
  compact?: boolean;
  /** Extra class name applied to the rendered <Image>, so parents (e.g. the
   *  card) can attach hover transforms through CSS Modules. */
  imageClassName?: string;
  /** A regeneration was just queued for THIS recipe + variant. Without it the
   *  hook can only spot a FIRST image (there is nothing on screen yet), so a
   *  re-shot photo never appeared until a manual refresh (#751). */
  generationInFlight?: boolean;
  /** Fires once when that regeneration ends — with the new url, or null when
   *  the poll gave up. Lets the page resolve its "regenerating…" toast off the
   *  one shared poller instead of running a second one. */
  onImageResolved?: (imageUrl: string | null) => void;
}

export function RecipeImage({
  recipeId,
  variantType,
  initialImageUrl,
  alt,
  sizes,
  priority,
  compact,
  imageClassName,
  generationInFlight,
  onImageResolved,
}: RecipeImageProps) {
  const t = useTranslations('recipes');
  const { imageUrl, isPending, justArrived } = useGeneratedImage(
    initialImageUrl,
    recipeId,
    variantType,
    { generationInFlight, onResolved: onImageResolved },
  );
  // Derived, not mirrored into state: the hook already knows the image arrived,
  // so a second setState would only be a cascading render of the same fact.
  // Only the dismissal is state, and it is set from a timer (never synchronously
  // inside the effect).
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const showArrivalAnnouncement = justArrived && !announcementDismissed;
  const spinnerClass = `${styles.spinner} ${compact ? styles.spinnerSm : styles.spinnerLg}`;

  // Clear the live-region announcement after the screen reader picks it up.
  useEffect(() => {
    if (!showArrivalAnnouncement) return;
    const t = setTimeout(() => setAnnouncementDismissed(true), 4000);
    return () => clearTimeout(t);
  }, [showArrivalAnnouncement]);

  if (imageUrl) {
    return (
      <>
        <Image
          src={imageUrl}
          alt={alt}
          fill
          priority={priority}
          sizes={sizes}
          className={[
            styles.image,
            justArrived ? styles.imageArrived : '',
            imageClassName ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {/* A regeneration in flight: the photo being replaced stays on screen
            (it is still the truth until the new one lands) under the same
            spinner + caption the empty state uses. */}
        {isPending && (
          <div className={styles.pendingOverlay} role="status" aria-live="polite">
            <Icon name="loading" className={spinnerClass} strokeWidth={1.5} aria-hidden="true" />
            {!compact && <p className={styles.overlayCaption}>{t('imageGenerating')}</p>}
            {compact && <span className={styles.srOnly}>{t('imageGenerating')}</span>}
          </div>
        )}
        {showArrivalAnnouncement && (
          <span role="status" aria-live="polite" className={styles.srOnly}>
            {t('imageReadyAnnouncement')}
          </span>
        )}
      </>
    );
  }

  const placeholderIconSize = compact ? styles.placeholderIconSm : styles.placeholderIconLg;

  return (
    <div
      className={styles.placeholder}
      aria-label={isPending ? t('imageGenerating') : undefined}
      role={isPending ? 'status' : undefined}
      aria-live={isPending ? 'polite' : undefined}
    >
      {isPending ? (
        <>
          <Icon name="loading" className={spinnerClass} strokeWidth={1.5} aria-hidden="true" />
          {!compact && <p className={styles.caption}>{t('imageGenerating')}</p>}
        </>
      ) : (
        <Icon
          name="fork-knife"
          className={placeholderIconSize}
          strokeWidth={1}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
